import { useCallback, useEffect, useState } from 'react';

import type {
  MachineListRow,
  UiOps,
  UiOpsAgent,
  UiOpsFailureType,
  UiOpsQueueItem,
  UiOpsSidecarProject,
  UiOpsSidecars,
  UiOpsSpendWindow,
  UiReviewQuality,
  UiReviewQualityWindow,
  UiSpendSeries,
  UiSuggestionPrecision,
  UiSupply,
} from '@nightshift/ui-types';
import { SPEND_SERIES_WINDOWS, SPEND_SERIES_DEFAULT_DAYS } from '@nightshift/ui-types';

import { Empty, Kicker, RefreshError, Spinner, StateChip } from '../components/primitives.tsx';
import { ChartLegend } from '../components/chart/legend.tsx';
import '../components/chart/chart.css';
import { SpendChart } from '../components/spend-chart.tsx';
import { assignSeriesColours, CHART_OTHER_COLOUR } from '../lib/chart-palette.ts';
import type { SpendDimension, SpendMeasure } from '../lib/spend-series.ts';
import { api, ApiError } from '../live/api.ts';
import { useLive, useStore } from '../live/context.tsx';
import { compactDuration, plural, relativeShort } from '../lib/format.ts';
import { bouncesFigure, firstPassFigure, queueByStateNote, spendFigure, spendNote } from '../lib/ops.ts';
import { stageStyle } from '../lib/palette.ts';
import type { ViewProps } from './board.tsx';
import './ops.css';

/**
 * The ops console — Dark Factory tables for attempts, machines, and dispatch.
 *
 * Tiles at the top for the numbers that decide whether anything needs doing.
 * Retry is not offered: a RateLimit is a Failed attempt, not a deferral to poke.
 * There is no audited bulk cancel of Queued attempts.
 */
export function OpsView({ onOpenTicket }: ViewProps) {
  const store = useStore();
  const state = useLive();
  const snapshot = state.snapshot;
  const now = state.tick;
  const [page, setPage] = useState<UiOps | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(true);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [spend, setSpend] = useState<UiSpendSeries | null>(null);
  const [spendDays, setSpendDays] = useState<number>(SPEND_SERIES_DEFAULT_DAYS);
  const [spendDimension, setSpendDimension] = useState<SpendDimension>('machine');
  const [spendMeasure, setSpendMeasure] = useState<SpendMeasure>('usd');
  const [reviewQuality, setReviewQuality] = useState<UiReviewQuality | null | 'error'>(null);

  const projectSlug = state.projectSlug;
  const revision = snapshot?.revision ?? 0;

  const load = useCallback(async () => {
    setPending(true);
    try {
      const next = await api.ops(projectSlug);
      setPage(next);
      setLoadedAt(Date.now());
      setError(null);
    } catch (caught: unknown) {
      if (caught instanceof ApiError && caught.status === 401) {
        setError(caught.message);
        return;
      }
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setPending(false);
    }
  }, [projectSlug]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  // Its own request, so changing the window does not refetch the whole console —
  // and so a failing series leaves the rest of ops readable.
  const loadSpend = useCallback(async () => {
    try {
      setSpend(await api.costSeries(projectSlug, spendDays));
    } catch {
      setSpend(null);
    }
  }, [projectSlug, spendDays]);

  useEffect(() => {
    void loadSpend();
  }, [loadSpend, revision]);

  // Seven- and thirty-day figures do not move with each live revision, so this loads per
  // project scope only; its failure leaves the rest of the console readable.
  useEffect(() => {
    let current = true;
    setReviewQuality(null);
    api.reviewQuality(projectSlug).then(
      (next) => {
        if (current) setReviewQuality(next);
      },
      () => {
        if (current) setReviewQuality('error');
      },
    );
    return () => {
      current = false;
    };
  }, [projectSlug]);

  const run = async (label: string, action: () => Promise<{ message: string }>) => {
    try {
      const result = await action();
      store.toast('good', result.message);
      await store.refetch();
      await load();
    } catch (caught) {
      store.toast('bad', `${label}: ${caught instanceof Error ? caught.message : String(caught)}`);
    }
  };

  if (error && !page) {
    return (
      <Empty
        title="Operations could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
        <Spinner size={20} />
      </div>
    );
  }

  const human = page.waiting.human;
  const orphans = page.agents.orphans;
  const stale = page.machines.stale;
  const failures = page.failures;

  return (
    <div className="ops" aria-busy={pending}>
      {error ? (
        <RefreshError what="Operations" error={error} since={loadedAt} now={now} onRetry={() => void load()} />
      ) : null}
      {snapshot && state.loadError && state.loadError !== 'session-expired' ? (
        <RefreshError
          what="The live snapshot"
          error={state.loadError}
          since={snapshot.now}
          now={now}
          onRetry={() => void store.refetch()}
        />
      ) : null}
      <div className="ops-tiles">
        <Tile
          label="Machines"
          value={`${page.machines.live}/${page.machines.total}`}
          tone={stale > 0 || page.machines.live === 0 ? 'var(--warn-text)' : undefined}
          note={
            page.machines.total === 0
              ? 'None registered — nothing can be claimed'
              : stale > 0
                ? `${plural(stale, 'stale heartbeat')} (35s)`
                : `${plural(page.machines.live, 'live heartbeat')}`
          }
        />
        <Tile
          label="Agents running"
          value={String(page.agents.running)}
          tone={orphans > 0 ? 'var(--warn-text)' : page.agents.running > 0 ? 'var(--good-text)' : undefined}
          note={
            orphans > 0
              ? `${plural(page.agents.running, 'attempt')} inside the runner timeout · ${plural(orphans, 'orphan')}`
              : page.agents.running === 0
                ? 'Nothing is executing right now'
                : runningNote(page.agents.items)
          }
        />
        <Tile
          label="Queue"
          value={String(page.queue.depth)}
          note={queueByStateNote(page.queue.byState)}
        />
        <Tile
          label="Spend today"
          value={spendFigure(page.spend.today.usd, page.spend.today.entries)}
          note={spendNote({ ...page.spend.today, window: 'today UTC' })}
        />
        <Tile
          label="Spend this week"
          value={spendFigure(page.spend.week.usd, page.spend.week.entries)}
          note={spendNote({ ...page.spend.week, window: `ISO ${page.spend.week.key} UTC` })}
        />
        <Tile
          label="Failures"
          value={String(failures.total)}
          tone={failures.total > 0 ? 'var(--danger-text)' : 'var(--good-text)'}
          note={
            failures.total === 0
              ? `No failed attempt in the last ${failures.windowDays} days UTC`
              : `${failures.windowDays} days UTC · ${failureSummary(failures.byType)}`
          }
        />
        <Tile
          label="Waiting on you"
          value={String(human)}
          tone={human > 0 ? 'var(--warn-text)' : 'var(--good-text)'}
          note={
            human === 0
              ? 'No open human-mode waiting request'
              : `${plural(human, 'open waiting request')} with wake mode human`
          }
        />
        <Tile
          label="Attempt limit"
          value={String(page.waiting.attemptLimit)}
          note={
            page.waiting.attemptLimit === 0
              ? 'No ticket is parked on attempt limit'
              : `${plural(page.waiting.attemptLimit, 'open park')} with reason attempt_limit`
          }
          tone={page.waiting.attemptLimit > 0 ? 'var(--warn-text)' : undefined}
        />
      </div>

      <p className="ops-relationship">
        {page.machines.live} of {page.machines.total}{' '}
        {page.machines.total === 1 ? 'machine' : 'machines'}{' '}
        {page.machines.live === 1 ? 'is' : 'are'} live.
        A heartbeat older than 35s is stale — a dead process is not counted as running.{' '}
        {page.agents.running} {page.agents.running === 1 ? 'attempt is' : 'attempts are'} inside the
        runner timeout
        {orphans > 0 ? `; ${plural(orphans, 'orphan')} sat past it` : ''}. The{' '}
        <strong>queue</strong> is Queued attempts (the ticket stays put). Spend is the cost
        ledger in UTC; a null dollar is unknown, never $0.
      </p>

      <ProductionPanel
        series={spend}
        days={spendDays}
        dimension={spendDimension}
        measure={spendMeasure}
        onDays={setSpendDays}
        onDimension={setSpendDimension}
        onMeasure={setSpendMeasure}
      />

      <ReviewQualityPanel quality={reviewQuality} />

      <div className="ops-grid">
        <div className="ops-stack">
          <MachinesPanel items={page.machines.items} now={now} slug={projectSlug} />
          <QueuePanel items={page.queue.items} onOpen={onOpenTicket} />
          <AgentsPanel items={page.agents.items} now={now} onOpen={onOpenTicket} />
          <SidecarsPanel sidecars={page.sidecars} now={now} onOpen={onOpenTicket} />
        </div>
        <div className="ops-stack">
          <FailuresPanel failures={failures} />
          <SpendPanel projects={page.spend.projects} />
          {snapshot ? (
            <ControlsPanel
              snapshotDispatchPaused={snapshot.dispatchPaused}
              supply={snapshot.supply}
              onRun={run}
            />
          ) : null}
          <DangerZone queued={page.queue.depth} />
          <p className="ops-elsewhere">
            Looking for the work rather than the machinery? The{' '}
            <a href={boardHref(projectSlug)}>board</a> has every open ticket, and{' '}
            <a href={runsHref(projectSlug)}>runs</a> has every attempt
            {failures.total > 0 ? ` — ${plural(failures.total, 'failure')} in this window` : ''}.
          </p>
        </div>
      </div>
    </div>
  );
}

function runningNote(items: readonly UiOpsAgent[]): string {
  return items
    .filter((row) => !row.orphaned)
    .slice(0, 2)
    .map((row) => `${row.state} on ${row.ticketKey}`)
    .join(', ');
}

function failureSummary(rows: readonly UiOpsFailureType[]): string {
  return rows
    .slice(0, 3)
    .map((row) => `${row.count} ${row.type}`)
    .join(', ');
}

function machinesSettingsHref(slug: string | null): string {
  const base = slug ? `/p/${encodeURIComponent(slug)}/settings` : '/settings';
  return `${base}?group=machines`;
}

function boardHref(slug: string | null): string {
  return slug ? `/p/${encodeURIComponent(slug)}/board` : '/board';
}

function runsHref(slug: string | null): string {
  return slug ? `/p/${encodeURIComponent(slug)}/runs` : '/runs';
}

function Tile({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: string;
  note: string;
  tone?: string;
}) {
  return (
    <div className="ops-tile">
      <span className="ops-tile-label">{label}</span>
      <span className="ops-tile-value num" style={{ color: tone }}>
        {value}
      </span>
      <span className="ops-tile-note truncate" title={note}>
        {note}
      </span>
    </div>
  );
}

function lampTone(row: MachineListRow): { live: boolean; attention: boolean; label: string } {
  if (row.live) return { live: true, attention: false, label: 'live' };
  if (row.stale) return { live: false, attention: true, label: 'stale' };
  return { live: false, attention: false, label: row.health };
}

function MachinesPanel({
  items,
  now,
  slug,
}: {
  items: readonly MachineListRow[];
  now: number;
  slug: string | null;
}) {
  return (
    <section className="ops-panel">
      <header className="panel-head">
        <Kicker count={items.length}>Machines</Kicker>
        <a className="ops-settings-link" href={machinesSettingsHref(slug)} style={{ marginLeft: 'auto' }}>
          Open in settings
        </a>
      </header>
      <div className="ops-pad">
        {items.length === 0 ? (
          <p className="ops-empty">
            No machine has registered. Until one does, attempts will queue forever — start the
            runner daemon on the execution host.
          </p>
        ) : (
          items.map((row) => {
            const lamp = lampTone(row);
            return (
              <div key={row.id} className="ops-machine">
                <span className="ops-machine-head">
                  <span
                    className={`lamp${lamp.attention ? ' lamp-breathe' : ''}`}
                    style={{
                      background: lamp.live
                        ? 'var(--good)'
                        : lamp.attention
                          ? 'var(--warn)'
                          : 'var(--danger)',
                    }}
                  />
                  <span className="mono" style={{ fontSize: 'var(--fs-small)' }}>
                    {row.name}
                  </span>
                  <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
                    {row.os}
                    {row.version ? ` · ${row.version}` : ''}
                  </span>
                  <span className="num dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
                    {lamp.label}
                  </span>
                </span>
                <span className="dim" style={{ fontSize: 'var(--fs-micro)', paddingLeft: 18 }}>
                  {row.offlineReason
                    ? row.offlineReason
                    : row.lastSeen === null
                      ? 'never sent a heartbeat'
                      : `heartbeat ${relativeShort(row.lastSeen, now)} ago`}
                </span>
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function QueuePanel({
  items,
  onOpen,
}: {
  items: readonly UiOpsQueueItem[];
  onOpen: (key: string) => void;
}) {
  return (
    <section className="ops-panel">
      <header className="panel-head">
        <Kicker count={items.length}>Queued attempts</Kicker>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          ticket stays put
        </span>
      </header>
      <div className="ops-body">
        {items.length === 0 ? (
          <p className="ops-empty">Nothing queued. Every attempt that exists is either running or finished.</p>
        ) : (
          items.map((item) => (
            <button type="button" className="ops-queue-row" key={item.attemptId} onClick={() => onOpen(item.ticketKey)}>
              <StateChip state={item.state} />
              <span className="mono">{item.ticketKey}</span>
              {item.activity ? <span className="dim">{item.activity}</span> : null}
              <span className="num dim" style={{ marginLeft: 'auto' }}>
                #{item.ordinal}
              </span>
            </button>
          ))
        )}
      </div>
    </section>
  );
}

function AgentsPanel({
  items,
  now,
  onOpen,
}: {
  items: readonly UiOpsAgent[];
  now: number;
  onOpen: (key: string) => void;
}) {
  return (
    <section className="ops-panel">
      <header className="panel-head">
        <Kicker count={items.length}>Live attempts</Kicker>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          elapsed against each attempt's runner timeout
        </span>
      </header>
      <div className="ops-body">
        {items.length === 0 ? (
          <p className="ops-empty">
            No attempt is Starting or Running. Either everything is done, everything is waiting on
            a person, or dispatch is paused.
          </p>
        ) : (
          items.map((item) => (
            <button
              type="button"
              className={`ops-queue-row${item.orphaned ? ' is-attention' : ''}`}
              key={item.attemptId}
              onClick={() => onOpen(item.ticketKey)}
            >
              <span
                className={`lamp${item.orphaned ? ' lamp-breathe' : ''}`}
                style={{ background: item.orphaned ? 'var(--warn)' : 'var(--good)' }}
              />
              <StateChip state={item.state} />
              <span className="mono">{item.ticketKey}</span>
              <span className="num dim" style={{ marginLeft: 'auto' }}>
                {item.orphaned ? 'orphan · ' : ''}
                {compactDuration(now - item.startedAt)}
              </span>
            </button>
          ))
        )}
      </div>
    </section>
  );
}

/**
 * The sidecar lane.
 *
 * Its own panel, below the attempts, because sidecars are a different kind of thing: they
 * take no worker slot, hold up no ticket, and stop for reasons an attempt never has (a switch
 * turned off, a route unset, a daily cap reached). Reporting them beside attempts would make
 * both numbers wrong. The per-project rows lead with the reasons nothing is happening,
 * because that is the question an operator brings to this panel.
 */
function SidecarsPanel({
  sidecars,
  now,
  onOpen,
}: {
  sidecars: UiOpsSidecars;
  now: number;
  onOpen: (key: string) => void;
}) {
  const money = (value: number): string => `$${value.toFixed(2)}`;
  return (
    <section className="ops-panel">
      <header className="panel-head">
        <Kicker count={sidecars.queued + sidecars.running}>Sidecars</Kicker>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          {sidecars.capacity.inUse} of {sidecars.capacity.slots} slots ·{' '}
          {money(sidecars.spentUsdToday)} spent, {money(sidecars.reservedUsdToday)} reserved today
        </span>
      </header>
      <div className="ops-body">
        {sidecars.projects.length === 0 ? (
          <p className="ops-empty">No project in view has sidecars configured.</p>
        ) : (
          <ul className="ops-sidecar-projects">
            {sidecars.projects.map((row) => (
              <li key={row.projectId} className="ops-sidecar-project">
                <span className="mono">{row.projectSlug}</span>
                <span className="dim">{sidecarProjectNote(row)}</span>
                <span className="num dim" style={{ marginLeft: 'auto' }}>
                  {money(row.spentUsd)}
                  {row.capUsd === null ? '' : ` of ${money(row.capUsd)}`}
                </span>
              </li>
            ))}
          </ul>
        )}
        {sidecars.items.length === 0 ? (
          <p className="ops-empty">
            Nothing is queued or running. Finished sidecars are in <a href="/runs">runs</a>.
          </p>
        ) : (
          sidecars.items.map((item) => (
            <button
              type="button"
              className="ops-queue-row"
              key={item.id}
              onClick={() => onOpen(item.ticketKey)}
            >
              <span className="chip ops-sidecar-kind">
                {item.kind === 'footprint' ? 'footprint' : item.kind === 'brief-cleanup' ? 'brief cleanup' : item.kind === 'creation-draft' ? 'quick add' : 'scan'}
              </span>
              <span className="mono">{item.ticketKey}</span>
              <span className="dim">{item.status}</span>
              <span className="num dim" style={{ marginLeft: 'auto' }}>
                {item.startedAt === null
                  ? `queued ${compactDuration(now - item.createdAt)}`
                  : compactDuration(now - item.startedAt)}
              </span>
            </button>
          ))
        )}
        <SuggestionPrecision projects={sidecars.projects} />
      </div>
    </section>
  );
}

const OUTCOME_COLOURS = assignSeriesColours(['accepted', 'dismissed', 'pending']);
const OUTCOMES = [
  { key: 'accepted', label: 'Accepted', colour: OUTCOME_COLOURS.get('accepted') ?? CHART_OTHER_COLOUR },
  { key: 'dismissed', label: 'Dismissed', colour: OUTCOME_COLOURS.get('dismissed') ?? CHART_OTHER_COLOUR },
  { key: 'pending', label: 'Pending', colour: OUTCOME_COLOURS.get('pending') ?? CHART_OTHER_COLOUR },
  // Left as unfilled track rather than a fourth colour: the neutral fails the palette
  // validator's normal-vision floor against teal, and three hues is the all-pairs limit.
  { key: 'retired', label: 'Retired undecided', colour: 'transparent' },
] as const;

type OutcomeKey = (typeof OUTCOMES)[number]['key'];

function outcomeCounts(row: UiSuggestionPrecision): Record<OutcomeKey, number> {
  return {
    accepted: row.accepted,
    dismissed: row.dismissed,
    pending: row.pending,
    retired: Math.max(0, row.made - row.accepted - row.dismissed - row.pending),
  };
}

function share(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '–';
}

/**
 * Whether the dependency scans are worth their cost: what they suggested over the last week
 * and month, what became of it, and what the footprints and scans cost in the same window.
 *
 * A table, because the numbers are small and are read one at a time. The bar in each row is
 * the same counts as a proportion, with a tooltip naming every segment and the legend below,
 * so no outcome is identified by colour alone.
 */
function SuggestionPrecision({ projects }: { projects: readonly UiOpsSidecarProject[] }) {
  const money = (value: number): string => `$${value.toFixed(2)}`;
  const shown = projects.filter((row) =>
    row.enabled || row.precision.some((window) => window.made > 0 || window.footprintUsd > 0 || window.scanUsd > 0),
  );
  if (shown.length === 0) return null;
  return (
    <div className="ops-precision" aria-label="Dependency suggestion precision">
      <p className="ops-precision-caption">
        Dependency suggestions from scans, counted when made, beside footprint and scan spend
      </p>
      {shown.map((project) => (
        <table className="ops-precision-table" key={project.projectId}>
          <thead>
            <tr>
              <th scope="col" className="mono ops-precision-project">{project.projectSlug}</th>
              {project.precision.map((window) => (
                <th scope="col" className="num" key={window.days}>{window.days} days</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope="row">Made</th>
              {project.precision.map((window) => <td className="num" key={window.days}>{window.made}</td>)}
            </tr>
            <tr>
              <th scope="row">Accepted</th>
              {project.precision.map((window) => (
                <td className="num" key={window.days}>
                  {window.accepted} <span className="dim">{share(window.accepted, window.made)}</span>
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Dismissed</th>
              {project.precision.map((window) => <td className="num" key={window.days}>{window.dismissed}</td>)}
            </tr>
            <tr>
              <th scope="row">Pending</th>
              {project.precision.map((window) => <td className="num" key={window.days}>{window.pending}</td>)}
            </tr>
            <tr>
              <th scope="row">Outcome</th>
              {project.precision.map((window) => (
                <td className="num" key={window.days}>
                  <OutcomeBar window={window} />
                </td>
              ))}
            </tr>
            <tr>
              <th scope="row">Footprint spend</th>
              {project.precision.map((window) => <td className="num" key={window.days}>{money(window.footprintUsd)}</td>)}
            </tr>
            <tr>
              <th scope="row">Scan spend</th>
              {project.precision.map((window) => <td className="num" key={window.days}>{money(window.scanUsd)}</td>)}
            </tr>
          </tbody>
        </table>
      ))}
      <ChartLegend
        entries={OUTCOMES.filter((outcome) => outcome.key !== 'retired').map(({ key, label, colour }) => ({ key, label, colour }))}
      />
      <p className="ops-precision-note">
        The unfilled part of a bar was retired without a decision: a later scan superseded it or a
        ticket finished. Accepted includes automatic acceptances nobody undid.
      </p>
    </div>
  );
}

/** The same counts as a proportion. Each segment names itself; the legend sits below. */
function OutcomeBar({ window }: { window: UiSuggestionPrecision }) {
  const counts = outcomeCounts(window);
  const label = window.made === 0
    ? 'No suggestions'
    : OUTCOMES.map((outcome) => `${outcome.label} ${counts[outcome.key]}`).join(', ');
  return (
    <span className="ops-precision-bar" role="img" aria-label={label}>
      {window.made === 0
        ? null
        : OUTCOMES.filter((outcome) => counts[outcome.key] > 0).map((outcome) => (
            <span
              key={outcome.key}
              className="ops-precision-segment"
              style={{ flexGrow: counts[outcome.key], background: outcome.colour }}
              title={`${outcome.label}: ${counts[outcome.key]} of ${window.made} (${share(counts[outcome.key], window.made)})`}
            />
          ))}
    </span>
  );
}

/** Why this project is or is not comparing anything, in the fewest words that are still true. */
function sidecarProjectNote(row: UiOpsSidecarProject): string {
  if (!row.enabled) return 'switched off';
  if (!row.routed) return 'no route configured';
  if (row.capped) return 'daily cap reached';
  const parts: string[] = [];
  if (row.running > 0) parts.push(`${row.running} running`);
  if (row.queued > 0) parts.push(`${row.queued} queued`);
  if (row.partialCoverage > 0) parts.push(`${row.partialCoverage} partly compared`);
  return parts.length > 0 ? parts.join(' · ') : 'idle';
}

function FailuresPanel({
  failures,
}: {
  failures: { windowDays: number; total: number; byType: readonly UiOpsFailureType[] };
}) {
  return (
    <section className="ops-panel">
      <header className="panel-head">
        <Kicker count={failures.total}>Failure types</Kicker>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          last {failures.windowDays} days UTC
        </span>
      </header>
      <div className="ops-pad">
        {failures.byType.length === 0 ? (
          <p className="ops-empty" style={{ padding: 0 }}>
            No failed attempt in this window. RateLimit is a type here, not a retry target.
          </p>
        ) : (
          failures.byType.map((row) => (
            <div className="ops-stepper-row" key={row.type}>
              <span style={{ flex: 1, fontSize: 'var(--fs-small)' }}>{row.type}</span>
              <span className="num" style={{ fontSize: 'var(--fs-small)' }}>
                {row.count}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

/**
 * Production over time: what each machine spent, on each project, day by day.
 *
 * Full width above the grid because a fortnight of stacked days needs the room,
 * and because it is the one panel that answers "is the factory producing" rather
 * than "is anything broken right now".
 */
function ProductionPanel({
  series,
  days,
  dimension,
  measure,
  onDays,
  onDimension,
  onMeasure,
}: {
  series: UiSpendSeries | null;
  days: number;
  dimension: SpendDimension;
  measure: SpendMeasure;
  onDays: (days: number) => void;
  onDimension: (dimension: SpendDimension) => void;
  onMeasure: (measure: SpendMeasure) => void;
}) {
  return (
    <section className="ops-panel ops-production">
      <header className="panel-head">
        <Kicker>Production</Kicker>
        <div className="ops-production-controls">
          <Segmented
            label="Stack by"
            value={dimension}
            options={[
              { value: 'machine', label: 'Machine' },
              { value: 'project', label: 'Project' },
            ]}
            onChange={onDimension}
          />
          <Segmented
            label="Measure"
            value={measure}
            options={[
              { value: 'usd', label: '$' },
              { value: 'tokens', label: 'Tokens' },
            ]}
            onChange={onMeasure}
          />
          <Segmented
            label="Window"
            value={days}
            options={SPEND_SERIES_WINDOWS.map((window) => ({
              value: window,
              label: `${window}d`,
            }))}
            onChange={onDays}
          />
        </div>
      </header>
      <div className="ops-pad">
        {series === null ? (
          <p className="ops-empty" style={{ padding: 0 }}>
            Spend series could not be read.
          </p>
        ) : (
          <SpendChart series={series} dimension={dimension} measure={measure} />
        )}
      </div>
    </section>
  );
}

/**
 * Whether implementations pass AI Review first time, and what a landed ticket cost in bounces.
 *
 * A table, not a chart: two windows of two ratios per project are read as numbers and compared
 * across a row, and no series identity rides on colour. Each ratio carries its counts, because
 * 100% of one ticket and 100% of forty are not the same claim.
 */
function ReviewQualityPanel({ quality }: { quality: UiReviewQuality | null | 'error' }) {
  const rows = quality && quality !== 'error' ? quality.projects : [];
  const windows = quality && quality !== 'error' ? quality.windows : [7, 30];
  const cell = (list: readonly UiReviewQualityWindow[], index: number, figure: (window: UiReviewQualityWindow) => string) => {
    const window = list[index];
    return window ? figure(window) : '—';
  };
  const columns = (list: readonly UiReviewQualityWindow[]) => (
    <>
      {windows.map((days, index) => (
        <td key={`first-${days}`} className="right num">
          {cell(list, index, firstPassFigure)}
        </td>
      ))}
      {windows.map((days, index) => (
        <td key={`bounce-${days}`} className="right num">
          {cell(list, index, bouncesFigure)}
        </td>
      ))}
    </>
  );
  return (
    <section className="ops-panel ops-review-quality" aria-label="AI review quality">
      <header className="panel-head">
        <Kicker count={rows.length}>AI review first pass</Kicker>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          last {windows.join(' and ')} days UTC
        </span>
      </header>
      <div className="ops-pad">
        {quality === null ? (
          <Spinner />
        ) : quality === 'error' ? (
          <p className="ops-empty" style={{ padding: 0 }}>
            Review quality could not be read.
          </p>
        ) : rows.length === 0 ? (
          <p className="ops-empty" style={{ padding: 0 }}>
            No AI Review decision and no landed ticket in the last {Math.max(...windows)} days.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Project</th>
                {windows.map((days) => (
                  <th key={`first-${days}`} scope="col" className="right">
                    First pass {days}d
                  </th>
                ))}
                {windows.map((days) => (
                  <th key={`bounce-${days}`} scope="col" className="right">
                    Bounces / landed {days}d
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.projectSlug}>
                  <td className="mono">{row.projectSlug}</td>
                  {columns(row.windows)}
                </tr>
              ))}
            </tbody>
            {rows.length > 1 ? (
              <tfoot>
                <tr>
                  <td>All listed projects</td>
                  {columns(quality.total)}
                </tr>
              </tfoot>
            ) : null}
          </table>
        )}
        <p className="ops-review-quality-note dim">
          First pass: tickets whose first AI Review decision fell in the window, and the share of
          those that passed it. Bounces / landed: AI Review failures sent back to Implementing,
          counted over the whole life of each ticket that merged in the window.
        </p>
      </div>
    </section>
  );
}

/** A small run of mutually exclusive buttons, grouped and labelled for assistive tech. */
function Segmented<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="ops-segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={String(option.value)}
          type="button"
          className={`ops-segment${option.value === value ? ' is-on' : ''}`}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function SpendPanel({ projects }: { projects: readonly UiOpsSpendWindow[] }) {
  return (
    <section className="ops-panel">
      <header className="panel-head">
        <Kicker count={projects.length}>Spend by project</Kicker>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          UTC ledger
        </span>
      </header>
      <div className="ops-pad">
        {projects.length === 0 ? (
          <p className="ops-empty" style={{ padding: 0 }}>
            No cost_entries in this scope. Cursor cost stays unknown — it is not drawn as $0.
          </p>
        ) : (
          projects.map((row) => (
            <div className="ops-stepper-row" key={row.key}>
              <span className="mono" style={{ flex: 1, fontSize: 'var(--fs-small)' }}>
                {row.key}
              </span>
              <span className="num" style={{ fontSize: 'var(--fs-small)' }}>
                {spendFigure(row.usd, row.entries)}
              </span>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function ControlsPanel({
  snapshotDispatchPaused,
  supply,
  onRun,
}: {
  snapshotDispatchPaused: boolean;
  supply: readonly UiSupply[];
  onRun: (label: string, action: () => Promise<{ message: string }>) => Promise<void>;
}) {
  const state = useLive();
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmPause, setConfirmPause] = useState(false);

  return (
    <section className="ops-panel">
      <header className="panel-head">
        <Kicker>Controls</Kicker>
        <span className="dim" style={{ marginLeft: 'auto', fontSize: 'var(--fs-micro)' }}>
          {state.projectSlug ? `scope: ${state.projectSlug}` : 'scope: global'}
        </span>
      </header>
      <div className="ops-pad">
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s3)' }}>
          <span style={{ flex: 1, fontSize: 'var(--fs-small)' }}>
            Dispatch
            <span className="dim" style={{ display: 'block', fontSize: 'var(--fs-micro)', lineHeight: 1.5 }}>
              Paused means no runner may claim a queued attempt. Agents already running finish
              normally.
            </span>
          </span>
          {confirmPause ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--s2)' }}>
              <span className="dim" style={{ fontSize: 'var(--fs-micro)' }}>
                Stop every project?
              </span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmPause(false)}>
                cancel
              </button>
              <button
                type="button"
                className="btn btn-warn btn-sm"
                disabled={busy === 'dispatch'}
                onClick={async () => {
                  setBusy('dispatch');
                  await onRun('Dispatch', () => api.setDispatchPaused(true));
                  setBusy(null);
                  setConfirmPause(false);
                }}
              >
                {busy === 'dispatch' ? <Spinner /> : null}
                pause everything
              </button>
            </span>
          ) : (
            <button
              type="button"
              className={snapshotDispatchPaused ? 'btn btn-accent btn-sm' : 'btn btn-warn btn-sm'}
              disabled={busy === 'dispatch'}
              onClick={async () => {
                if (!snapshotDispatchPaused) {
                  setConfirmPause(true);
                  return;
                }
                setBusy('dispatch');
                await onRun('Dispatch', () => api.setDispatchPaused(false));
                setBusy(null);
              }}
            >
              {busy === 'dispatch' ? <Spinner /> : null}
              {snapshotDispatchPaused ? 'resume' : 'pause'}
            </button>
          )}
        </div>

        <div className="hair-t" style={{ paddingTop: 'var(--s3)' }}>
          <span className="kicker">Concurrency</span>
          <p className="dim" style={{ fontSize: 'var(--fs-micro)', lineHeight: 1.55, margin: '4px 0 8px' }}>
            Machine capacity is managed from the shared Machines registry.
          </p>
          {supply.map((item) => (
            <div className="ops-stepper-row" key={item.stage} style={{ marginBottom: 6 }}>
              <span className="dot" style={{ background: stageStyle(item.stage).line, width: 7, height: 7 }} />
              <span style={{ flex: 1, fontSize: 'var(--fs-small)' }}>
                {item.stage}
                {item.capSource === 'central' ? (
                  <span className="dim" style={{ marginLeft: 6, fontSize: 'var(--fs-micro)' }}>
                    set in settings
                  </span>
                ) : null}
              </span>
              <span className="num" style={{ fontSize: 'var(--fs-small)' }}>
                {item.inFlight}
                <span className="dim">/{item.cap}</span>
              </span>
            </div>
          ))}
          <a className="ops-settings-link" href={machinesSettingsHref(state.projectSlug)}>
            Change machine capacity in Machines
          </a>
        </div>
      </div>
    </section>
  );
}

function DangerZone({ queued }: { queued: number }) {
  return (
    <section className="ops-danger">
      <span className="kicker" style={{ color: 'var(--danger-text)' }}>
        Danger zone
      </span>
      <button type="button" className="btn btn-sm" disabled style={{ alignSelf: 'flex-start' }}>
        {queued === 0 ? 'Nothing queued to cancel' : 'No bulk cancel yet'}
      </button>
      <span className="dim" style={{ fontSize: 'var(--fs-micro)', lineHeight: 1.55 }}>
        There is no audited bulk command for Queued attempts. Cancel a ticket from its drawer.
        Retry is not offered here — a RateLimit waiting is not a Failed row to poke.
      </span>
    </section>
  );
}

